// src/components/VistaSemana.jsx
import React, { useState } from "react";
import moment from "moment";
import ModalCalendar from "./ModalCalendar";

/* ───── helpers ───── */
const asMoment = (d) => {
  if (!d) return moment.invalid();
  if (moment.isMoment(d)) return d;
  if (d.toDate) return moment(d.toDate());
  if (typeof d.seconds === "number") return moment.unix(d.seconds);
  return moment(d);
};
const minutesOfDay = (d) =>
  asMoment(d).diff(asMoment(d).clone().startOf("day"), "minutes");

/* ───── layout ───── */
const HOURS = 24; // 00 – 23
const ROW_HOUR_PX = 64; // alto de cada hora
const CARD_H = 64; // alto fijo de tarjeta
const COL_MARGIN_PX = 6; // margen lateral interno
const BORDER_PX = 1; // grosor de línea gris
const COL_W_PCT = 100 / 7; // ancho de una columna en %
const GRID_CELLS = Array.from({ length: 7 * HOURS });

/* base tarjeta */
const CARD_BASE = {
  height: CARD_H,
  minHeight: CARD_H,
  maxHeight: CARD_H,
  borderRadius: 4,
  boxSizing: "border-box",
  padding: "4px 6px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  overflow: "hidden",
  lineHeight: "14px",
  fontSize: "11px",
};

/* estilo por tipo */
const getColorClasses = (ev) => {
  if (ev.resource === "tarea" && ev.color) return "";
  switch (ev.resource) {
    case "cotizacion":
      return "bg-blue-100 text-blue-600";
    case "evento":
      return "bg-green-100 text-green-600";
    case "festivo":
      return "bg-red-100 text-red-600";
    default:
      return "bg-gray-200 text-gray-800";
  }
};

/* rango legible */
const fmtRange = (ev) =>
  `${asMoment(ev.start).format("HH:mm")} – ${asMoment(ev.end).format("HH:mm")}`;

/* ───── componente ───── */
export default function VistaSemana({
  events,
  currentDate,
  onAddEvent,
  onDeleteEvent,
}) {
  /* modal preview */
  const [calOpen, setCalOpen] = useState(false);
  const [calEvents, setCalEvents] = useState([]);
  const openModalWith = (ev) => {
    setCalEvents([ev]);
    setCalOpen(true);
  };

  /* semana actual */
  const weekStart = asMoment(currentDate).startOf("isoWeek"); // lunes
  const weekEnd = weekStart.clone().add(6, "days");

  const days = Array.from({ length: 7 }, (_, i) =>
    weekStart.clone().add(i, "days")
  );
  const hours = Array.from(
    { length: HOURS },
    (_, i) => `${String(i).padStart(2, "0")}:00`
  );

  /* clasificación de eventos */
  const allDayByDay = {};
  const timedByDay = {};

  events.forEach((ev) => {
    const s0 = asMoment(ev.start);
    const e0 = asMoment(ev.end);
    if (!s0.isValid() || !e0.isValid()) return;
    if (e0.isBefore(weekStart, "day") || s0.isAfter(weekEnd, "day")) return;

    const isAll =
      ev.allDay &&
      s0.isSame(s0.clone().startOf("day")) &&
      e0.isSame(e0.clone().endOf("day"));

    if (isAll) {
      let cur = moment.max(s0, weekStart);
      const last = moment.min(e0, weekEnd);
      while (cur.isSameOrBefore(last, "day")) {
        (allDayByDay[cur.format("YYYY-MM-DD")] ||= []).push(ev);
        cur.add(1, "day");
      }
      return;
    }

    const segStartDay = moment.max(s0, weekStart).clone().startOf("day");
    const segEndDay = moment.min(e0, weekEnd).clone().startOf("day");
    const spanDays = segEndDay.diff(segStartDay, "days") + 1;

    const seg = {
      ...ev,
      segStart: moment.max(s0, weekStart),
      segEnd: e0,
      spanDays,
    };
    (timedByDay[segStartDay.format("YYYY-MM-DD")] ||= []).push(seg);
  });

  /* ------------- render ------------- */
  return (
    <>
      <div className="flex flex-col bg-gray-50">
        {/* encabezado */}
        <div className="grid grid-cols-8 select-none">
          {/* ← primera columna vacía ahora con reloj */}
          <div className="bg-gray-100 h-12 border-r flex items-center justify-center">
            <i className="bi bi-clock text-gray-500 text-xl" />
          </div>

          {days.map((d) => (
            <div
              key={d}
              className="h-12 bg-gray-100 border-r flex flex-col items-center justify-center"
            >
              <span className="text-sm text-gray-600 uppercase">
                {d.format("dddd,")}
              </span>
              <span className="text-md font-semibold text-gray-800">
                {d.format("D")}
              </span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-8 overflow-auto border-t">
          {/* eje horas */}
          <div className="border-r bg-gray-100 select-none">
            {hours.map((h) => (
              <div
                key={h}
                className="h-16 flex items-center justify-center text-gray-500 text-xs border-b last:border-b-0"
              >
                {h}
              </div>
            ))}
          </div>

          {/* malla + eventos */}
          <div
            className="relative col-span-7"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gridTemplateRows: `repeat(${HOURS}, ${ROW_HOUR_PX}px)`,
            }}
          >
            {/* malla */}
            <div
              className="absolute inset-0 grid pointer-events-none z-0"
              style={{
                gridTemplateColumns: "repeat(7, 1fr)",
                gridTemplateRows: `repeat(${HOURS}, ${ROW_HOUR_PX}px)`,
              }}
            >
              {GRID_CELLS.map((_, i) => (
                <div
                  key={i}
                  className="border border-gray-200 last:border-b-0"
                />
              ))}
            </div>

            {/* eventos */}
            {days.map((day, colIdx) => {
              const dayKey = day.format("YYYY-MM-DD");
              const leftPct = COL_W_PCT * colIdx;

              const stack = [];

              /* all-day */
              (allDayByDay[dayKey] || []).forEach((ev, i) => {
                const inline =
                  ev.resource === "tarea" && ev.color
                    ? { backgroundColor: ev.color, color: "#1F2937" }
                    : {};

                stack.push(
                  <div
                    key={`ad-${ev.id}-${i}`}
                    className={`absolute z-10 truncate ${getColorClasses(ev)}`}
                    style={{
                      ...CARD_BASE,
                      top: 2 + i * (CARD_H + 2),
                      left: `calc(${leftPct}% + ${COL_MARGIN_PX}px + ${
                        colIdx * BORDER_PX
                      }px)`,
                      width: `calc(${COL_W_PCT}% - ${COL_MARGIN_PX * 2}px)`,
                      cursor: "pointer",
                      ...inline,
                    }}
                    onClick={() => openModalWith(ev)}
                  >
                    <span className="font-medium truncate">{ev.title}</span>
                  </div>
                );
              });

              /* con hora */
              const list = (timedByDay[dayKey] || []).sort(
                (a, b) =>
                  asMoment(a.segStart) - asMoment(b.segStart) ||
                  b.spanDays - a.spanDays
              );

              let lastBottom = 0;
              list.forEach((seg) => {
                const s = asMoment(seg.segStart);
                const topPx = minutesOfDay(s) * (ROW_HOUR_PX / 60);
                const width = `calc(${COL_W_PCT * seg.spanDays}% - ${
                  COL_MARGIN_PX * 2 + (seg.spanDays - 1) * BORDER_PX
                }px)`;
                const finalTop = Math.max(topPx, lastBottom + 2);
                lastBottom = finalTop + CARD_H;

                const inline =
                  seg.resource === "tarea" && seg.color
                    ? { backgroundColor: seg.color, color: "#1F2937" }
                    : {};

                stack.push(
                  <div
                    key={`tm-${seg.id}-${s.format()}`}
                    className={`absolute z-20 shadow-sm truncate ${getColorClasses(
                      seg
                    )}`}
                    style={{
                      ...CARD_BASE,
                      top: finalTop,
                      left: `calc(${leftPct}% + ${COL_MARGIN_PX}px + ${
                        colIdx * BORDER_PX
                      }px)`,
                      width,
                      cursor: "pointer",
                      ...inline,
                    }}
                    onClick={() => openModalWith(seg)}
                  >
                    <div className="flex justify-between items-center font-medium">
                      <span className="truncate">{seg.title}</span>
                      <span>{s.format("HH:mm")}</span>
                    </div>
                    <span className="font-semibold text-[10px] truncate">
                      {fmtRange(seg)}
                    </span>
                  </div>
                );
              });

              return <React.Fragment key={dayKey}>{stack}</React.Fragment>;
            })}
          </div>
        </div>
      </div>

      {/* modal de edición / vista previa */}
      <ModalCalendar
        isOpen={calOpen}
        onClose={() => setCalOpen(false)}
        events={calEvents}
        onEdit={(ev) => onAddEvent?.(ev)}
        onDelete={(ev) => onDeleteEvent?.(ev)}
      />
    </>
  );
}
